import {Injectable} from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {AppConfigService} from '../core/config/app-config.service';
import {Observable} from 'rxjs';
import {Employee} from '../admin/models/employee.model';
import {Team} from '../admin/models/team.model';
import {PublicHoliday} from '../admin/models/publicHoliday.model';
import {HrDocs} from '../admin/models/hrDocs.model';
import {AdUser} from '../admin/models/ad-user';
import {DatePipe} from '@angular/common';
import {AbsenceService} from '../shared/globals/absence-service.service';
import {ImportExcel} from '../admin/models/importExcel.model';
import {AuthenticationService} from '../core/authentication/authentication.service';

@Injectable({
    providedIn: 'root'
})
export class HrService {
    private readonly baseUrl;
    private readonly header: any;

    constructor(private readonly httpClient: HttpClient,
                private readonly configService: AppConfigService,
                private readonly authenticationService: AuthenticationService,
                private readonly absenceService: AbsenceService) {
        this.baseUrl = configService.getConfig().baseUrl;
        this.header = {'Authorization': 'Bearer ' + this.authenticationService.getAccessToken()};
    }

    /**
     * passes a new employee to the api  to be saved with their role
     * @param data Employee data to be sent
     * @param group Employee role to be sent
     */
    public saveEmployee(data: Employee, group: any): Observable<Employee> {
        return this.absenceService.empPostRequest(this.baseUrl, '/employee/role/', group, data);
    }

    /**
     * passes a new employee to the api to be updated with their role
     * @param data Employee data to be sent
     * @param group Employee role to be sent
     */
    public updateEmployee(data: Employee, group: any): Observable<Employee> {
        return this.absenceService.empPutRequest(this.baseUrl, '/employee/role/', group, data);
    }

    /**
     * saves a new team details in the backend
     * @param data Team data to be saved
     */
    public saveTeam(data: Team): Observable<Team> {
        return this.absenceService.postRequest(this.baseUrl, '/team', data);
    }

    /**
     * Updates a teams details in the backend
     * @param data Team data to be saved
     */
    public updateTeam(data: Team): Observable<Team> {
        return this.absenceService.putRequest(this.baseUrl, '/team', data);
    }

    /**
     * makes an api call to get all teams
     */
    public getTeams(): Observable<Team[]> {
        return this.absenceService.getRequest(this.baseUrl, '/team');
    }

    /**
     * makes an api call to get all teams
     */
    public deleteEmployee(id: string): Observable<null> {
        return this.absenceService.deleteRequest(this.baseUrl, '/employee/' + id);
    }

    /**
     * makes an api call to get all teams
     */
    public editWorkAbroad(data: any): Observable<any> {
        return this.absenceService.putRequest(this.baseUrl, '/work-abroad/hr', data);
    }

    /**
     * makes a call to the people service api to search for employees
     * @param searchQuery the data passed to be searched
     */
    public searchForEmployees(searchQuery: any): Observable<HttpResponse<AdUser[]>> {
        const params:
            HttpParams = new HttpParams()
            .set('query', searchQuery ?? '');

        return this.httpClient.get<AdUser[]>(this.baseUrl + '/people/services/search', {
            params,
            observe: 'response',
            headers: this.header
        });
    }

    /**
     * mathod takes the employees gmin and creates the url to search for their photo
     * @param userGmin gmin to  locate photo url
     */
    public getPhotoUrl(userGmin: string): string {
        const positionThree = 3;
        const positionSix = 6;
        const firstThreeOfGmin = userGmin.substring(0, positionThree);
        const secondThreeOfGmin = userGmin.substring(positionThree, positionSix);
        return 'https://peoplefinder.gm.com/photos/'
            .concat(firstThreeOfGmin)
            .concat('/')
            .concat(secondThreeOfGmin)
            .concat('/')
            .concat(userGmin)
            .concat('.jpg');
    }

    /**
     * method to display image
     * @param event on search
     */
    public displayDefaultImage(event: any) {
        const imageAsElement = event.target as HTMLImageElement;
        imageAsElement.src = 'assets/images/defaultProfilePic.png';
    }

    /**
     * makes an api call to get all employees by location
     */
    public getEmployeeByLocation(countrySettingsCode: string): Observable<Employee[]> {
        return this.absenceService.getRequest(this.baseUrl, '/employee/region/' + countrySettingsCode);
    }

    /**
     * Submits a new public holiday to the backend
     * @param data the public holiday to be submitted
     */
    public submitPublicHoliday(data: PublicHoliday): Observable<null> {
        return this.absenceService.postRequest(this.baseUrl, '/public-holiday', data);
    }

    /**
     * makes an api call to get all Public Holidays by location
     */
    public getPublicHoliday(countrySettingsCode: string): Observable<PublicHoliday[]> {
        return this.absenceService.getRequest(this.baseUrl, '/public-holiday/region/' + countrySettingsCode);
    }

    /**
     * makes an api call to delete public holiday by id
     */
    public deletePublicHoliday(id: string): Observable<null> {
       return this.absenceService.deleteRequest(this.baseUrl, '/public-holiday/cancel/'+ id);

    }

    /**
     * Returns a string with the year only
     * @param dateString The datestring to split
     */
    public getYear(dateString: string): string {
        return dateString.substring(6, 10);
    }

    /**
     * Returns List of public holiday for the given year
     * @param data The List of public holidays
     * @param year year value
     */
    public segregateYearAndPublicHoliday(data: PublicHoliday[], year: string) {
        const collectiveData: PublicHoliday[] = [];
        data.forEach((value) => {
            if (this.getYear(value.holidayDate) === year) {
                collectiveData.push(value);
            }
        });
        return collectiveData;
    }

    /**
     * Returns number of employee, full time, part time and intern details
     * @param data The List of employee details
     * @param team team value
     * @param year year value
     */
    public calculateCardOfEmployee(data: any, team: string[], year: string) {
        let fullTime = 0;
        let partTime = 0;
        let interns = 0;
        let employeeNum = 0;
        let contractors = 0;
        let fixedTerm = 0;
        data.forEach((val: any) => {
            if ((team == val.teamName || team.length === 0) && val.year == year) {
                employeeNum++;
                if (val.contractType === 'FULL-TIME-PERMANENT' || val.contractType === 'FULL_TIME_PERMANENT') {
                    fullTime++;
                } else if (val.contractType === 'PART-TIME' || val.contractType === 'PART_TIME') {
                    partTime++;
                } else if (val.contractType === 'INTERN') {
                    interns++;
                } else if (val.contractType === 'CONTRACTOR') {
                    contractors++;
                } else if (val.contractType === 'FIXED_TERM') {
                    fixedTerm++;
                }
            }
        });
        return {employeeNum, fullTime, partTime, interns, contractors, fixedTerm}
    }

    /**
     * Return if the data is successfully submitted
     * @param data the data to be processed
     */
    public submitPdfFile(data: HrDocs): Observable<HrDocs> {
        return this.absenceService.postRequest(this.baseUrl, '/hr-docs', data);
    }

    /**
     * Return list of HrDocs
     */
    public getPdf(): Observable<HrDocs[]> {
        return this.absenceService.getRequest(this.baseUrl, '/hr-docs');
    }

    /**
     * Return if the data is successfully updated
     * @param data the data to be processed
     */
    public updatePdf(data: HrDocs): Observable<HrDocs> {
        return this.absenceService.putRequest(this.baseUrl, '/hr-docs', data);
    }

    /**
     * Converts a date string to format dd/MM/yyyy. The api requires this formatting
     * @param dateString The string to convert
     */
    public convertDateFormatForAPI(dateString: string): string {
        if (dateString.charAt(2) === '/') {
            return dateString;
        } else {
            const newDateString = new DatePipe('en-US').transform(dateString, 'dd/MM/yyyy');
            return newDateString ? newDateString : '';
        }
    }

    /**
     * This method gets all employee details
     * @param data Employee object to loop through
     * @param nameOfTeam Map to determine team name
     * @param year Year for Vacation allowance
     */
    public allEmployeeData(data: Employee[], nameOfTeam: Map<string, string>, year: string): any[] {
        let employeeList: any[] = [];
        const workAllowanceRemaining = 0;
        const workTotal = 0;
        data.forEach((value) => {
            value.employeeVacationAllowance.forEach((dat: any) => {
                if (year == String(dat.year)) {
                    employeeList.push({
                        firstName: value.firstName,
                        lastName: value.lastName,
                        gmin: value.gmin,
                        email: value.emailAddress,
                        startDate: value.dateStarted,
                        contractType: value.contractType,
                        location: value.location,
                        vacationAllowance: dat.vacationAllowance,
                        holidayTaken: dat.vacationAllowance - dat.vacationAllowanceRemaining,
                        vacationAllowanceRemaining: dat.vacationAllowanceRemaining,
                        vacationAllowanceCarryover: dat.vacationAllowanceCarryover,
                        teamName: nameOfTeam.get(value.teamId),
                        workAbroadAllowanceRemaining: workAllowanceRemaining,
                        workAllowanceTotal: workTotal
                    });
                }
            });
        });
        if (employeeList.length == 0) {
            employeeList = this.extractEmployeeDetails(data, nameOfTeam)
        }
        return employeeList
    }

    /**
     * This method gets all employee details and assigns the vacation allowance as zero
     * if there is no vacation allowance in database
     * @param data Employee object to loop through
     * @param nameOfTeam Map to determine team name
     */
    public extractEmployeeDetails(data: Employee[], nameOfTeam: Map<string, string>): any[] {
        const employeeList: any[] = [];
        data.forEach((value) => {
            employeeList.push({
                firstName: value.firstName,
                lastName: value.lastName,
                gmin: value.gmin,
                email: value.emailAddress,
                startDate: value.dateStarted,
                contractType: value.contractType,
                vacationAllowance: 0,
                holidayTaken: 0,
                vacationAllowanceRemaining: 0,
                vacationAllowanceCarryover: 0,
                teamName: nameOfTeam.get(value.teamId),
                workAbroadAllowanceRemaining: 0,
                workAllowanceTotal: 0,
            });
        });
        return employeeList;
    }

    /**
     * Saves employee team data using the synchronous import API
     * @param data Array of ImportExcel objects
     * @returns Observable with import result information including status, successfulRows, errorRows, etc.
     */
    public saveEmployeeTeam(data: ImportExcel[]): Observable<any> {
        return this.absenceService.empTeamPutRequest(this.baseUrl, '/workday/employee/import', data);
    }

    public downloadEmployeeDetailsExcel(countrySettingsCode: String): Observable<Blob> {
        return this.absenceService.downloadExcelPostRequest(this.baseUrl, '/employee/details/export/excel/region/' + countrySettingsCode);
    }

    public downloadEmployeeWorkAbroadDetailsExcel(countrySettingsCode: String): Observable<Blob> {
        return this.absenceService.downloadExcelPostRequest(this.baseUrl, '/employee/work/abroad/export/excel/region/' + countrySettingsCode);
    }

    public getData(data: Employee[], nameOfTeam: Map<string, string>): any {
        const pendingVacation: any[] = [];
        const allVacationRequest: any[] = [];
        const pendingWork: any[] = [];
        const allWorkRequest: any[] = [];
        const approvedVacation: any[] =[];
        data.forEach((value) => {
            pendingVacation.push(...this.vacationRequest(value, nameOfTeam));
            allVacationRequest.push(...this.allVacationRequest(value, nameOfTeam));
            pendingWork.push(...this.workAbroadRequest(value, nameOfTeam));
            allWorkRequest.push(...this.allWorkAbroadRequest(value, nameOfTeam));
            approvedVacation.push(...this.vacationRequest(value, nameOfTeam));
        });
        return {pendingVacation, allVacationRequest, pendingWork, allWorkRequest, approvedVacation};
    }

    /**
     * This method calculate the work abroad requests to be populated in vacation card
     * @param data Employee object to loop through
     * @param year
     * @param team list of team name
     */
    public calculateDisplayOfWork(data: any[], year: string, team: string[]) {
        let annualWork = 0;
        let remainingWorkDays = 0;
        let difference = 0;
        let workDaysUsed = 0;
        for (let i = 0; i < data.length; i++) {
            if (data[i].year.includes(year) && i === 0 && team.length == 0 || data[i].year.includes(year) && team == data[i].teamName) {
                annualWork += Number(data[i].workAllowanceTotal);
                remainingWorkDays += Number(data[i].workAbroadAllowanceRemaining);
                difference = annualWork - remainingWorkDays;
                workDaysUsed += difference;
            }
            if (data[i].year.includes(year) && i >= 1 && data[i].firstName !== data[i - 1].firstName) {
                annualWork += Number(data[i].workAllowanceTotal);
                remainingWorkDays += Number(data[i].workAbroadAllowanceRemaining);
                difference = annualWork - remainingWorkDays;
                workDaysUsed += difference;
            }
        }
        return {annualWork, remainingWorkDays, workDaysUsed};
    }


    /**
     * This method calculate the pending work abroad days requests for work abroad.
     * @param data Employee object to loop through
     * @param team list of team names
     * @param year year to be calculated
     */
    public calculatePendingDaysOfWork(data: any[], year: string, team: string[]) {
        let pendingWorkDays = 0;
        let pendingRequests = 0;
        data.forEach((val) => {
            if (team.length == 0 || team == val.teamName) {
                pendingWorkDays += Number(val.numberOfWorkAbroadDays);
                pendingRequests++;
            }
        });
        return {pendingWorkDays, pendingRequests};
    }

    /**
     * This method gets all pending work abroad requests
     * @param data Employee object to loop through
     * @param nameOfTeam Map to determine team name
     */
    public workAbroadRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const pendingWork: any[] = [];
        let numberOfAnnualLeave = 0
        data.workAbroad.forEach((dat: any) => {
            numberOfAnnualLeave = 0;
            if (data.workAbroad.length > 0) {
                if (dat.vacations) {
                    dat.vacations.forEach((value: any) => {
                        numberOfAnnualLeave += value.numberOfAnnualLeaveDays;
                    });
                }
                pendingWork.push({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    country: dat.country,
                    dateCreated: dat.dateCreated,
                    startDate: dat.startDate,
                    endDate: dat.endDate,
                    numberOfWorkAbroadDays: dat.numberOfWorkAbroadDays,
                    requestStatus: dat.requestStatus,
                    lastDayWorking: dat.lastDayWorking,
                    firstDayBack: dat.firstDayBack,
                    vacations: dat.vacations,
                    employeeId: dat.employeeId,
                    updatedBy: dat.updatedBy,
                    id: dat.id,
                    createdTimestamp: dat.createdTimestamp,
                    teamName: nameOfTeam.get(data.teamId),
                    year: this.getYear(dat.endDate),
                    numberOfAnnualLeaveDays: numberOfAnnualLeave,
                    gmin: data.gmin
                });
            }
        });
        return pendingWork;
    }

    /**
     * This method gets all vacation requests
     * @param data Employee
     * @param nameOfTeam Map to determine team name
     */
    public allVacationRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const allVacationRequest: any[] = [];
        let workAllowanceRemaining: number = 0
        let workTotal: number = 0
        data.employeeWorkAbroadAllowance.forEach((wok: any) => {
            workAllowanceRemaining = wok.allowanceRemaining;
            workTotal = wok.allowance;
        });
        data.employeeVacationAllowance.forEach((dat: any) => {
            allVacationRequest.push({
                firstName: data.firstName,
                lastName: data.lastName,
                gmin: data.gmin,
                email: data.emailAddress,
                startDate: data.dateStarted,
                contractType: data.contractType,
                location: data.location,
                vacationAllowance: dat.vacationAllowance,
                holidayTaken: dat.vacationAllowance - dat.vacationAllowanceRemaining,
                vacationAllowanceRemaining: dat.vacationAllowanceRemaining,
                vacationAllowanceCarryover: dat.vacationAllowanceCarryover,
                year: dat.year,
                teamName: nameOfTeam.get(data.teamId),
                workAbroadAllowanceRemaining: workAllowanceRemaining,
                workAllowanceTotal: workTotal,
                employeeYear: this.getYear(data.dateStarted)
            });
        });
        return allVacationRequest
    }

    /**
     * This method gets the pending requests for the vacation table from each employee
     * @param data An employee object
     * @param nameOfTeam Map to determine team name
     */
    public vacationRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const pendingVacation: any = [];
        data.vacation.forEach((dat: any) => {
            if (data.vacation.length > 0) {
                pendingVacation.push({
                    id: dat.id,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    dateCreated: dat.dateCreated,
                    halfDay: dat.halfDay,
                    startDate: dat.startDate,
                    endDate: dat.endDate,
                    numberOfAnnualLeaveDays: dat.numberOfAnnualLeaveDays,
                    requestStatus: dat.requestStatus,
                    absenceReason: dat.absenceReason,
                    employeeId: dat.employeeId,
                    region: dat.region,
                    createdBy: dat.createdBy,
                    createdTimestamp: dat.createdTimestamp,
                    teamName: nameOfTeam.get(data.teamId),
                    absenceType: dat.absenceType,
                    year: this.getYear(dat.endDate),
                    gmin: data.gmin
                });
            }
        });
        return pendingVacation;
    }

    /**
     * This method gets all workAbroad requests
     * @param data Employee object to loop through
     * @param nameOfTeam Map to determine team name
     */
    public allWorkAbroadRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const allWorkRequest: any[] = [];
        let numberOfAnnualLeave = 0
        let workAllowanceRemaining: number = 0
        let workTotal: number = 0
        data.employeeWorkAbroadAllowance.forEach((wok: any) => {
            workAllowanceRemaining = wok.allowanceRemaining;
            workTotal = wok.allowance;
        });
        data.workAbroad.forEach((dat: any) => {
            if (dat.vacations) {
                numberOfAnnualLeave = 0;
                dat.vacations.forEach((value: any) => {
                    numberOfAnnualLeave += value.numberOfAnnualLeaveDays;
                });
            }
            allWorkRequest.push({
                firstName: data.firstName,
                lastName: data.lastName,
                contractType: data.contractType,
                country: dat.country,
                dateCreated: dat.dateCreated,
                startDate: dat.startDate,
                endDate: dat.endDate,
                numberOfWorkAbroadDays: dat.numberOfWorkAbroadDays,
                workAbroadNegativeCarryOver: dat.workAbroadNegativeCarryOver,
                requestStatus: dat.requestStatus,
                lastDayWorking: dat.lastDayWorking,
                firstDayBack: dat.firstDayBack,
                vacations: dat.vacations,
                employeeId: dat.employeeId,
                updatedBy: dat.updatedBy,
                id: dat.id,
                createdTimestamp: dat.createdTimestamp,
                teamName: nameOfTeam.get(data.teamId),
                year: this.getYear(dat.endDate),
                numberOfAnnualLeaveDays: numberOfAnnualLeave,
                gmin: data.gmin,
                workAbroadAllowanceRemaining: workAllowanceRemaining,
                workAllowanceTotal: workTotal,
            });
        });
        return allWorkRequest
    }

    public deleteVacationRequest(id: string): Observable<null> {
        return this.absenceService.deleteRequest(this.baseUrl, '/vacation/hr/cancel/' + id);
    }
}
